import React, { useRef, useEffect, useState } from 'react';

const StatusLine = ({ vimMode, activeFile, commandText, commandMode, onCommand, onCommandCancel, cursorLine, lineCount, contentRef }) => {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [scrollPct, setScrollPct] = useState('All');

  // Track scroll position
  useEffect(() => {
    const el = contentRef?.current;
    if (!el) return;
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) {
        setScrollPct('All');
      } else if (scrollTop <= 1) {
        setScrollPct('Top');
      } else if (scrollTop >= maxScroll - 1) {
        setScrollPct('Bot');
      } else {
        setScrollPct(`${Math.round((scrollTop / maxScroll) * 100)}%`);
      }
    };
    update();
    el.addEventListener('scroll', update);
    return () => el.removeEventListener('scroll', update);
  }, [contentRef, activeFile]);

  // Focus input when command mode activates
  useEffect(() => {
    if (commandMode && inputRef.current) {
      setInputValue('');
      inputRef.current.focus();
    }
  }, [commandMode]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onCommand(inputValue);
      setInputValue('');
    } else if (e.key === 'Escape') {
      onCommandCancel();
      setInputValue('');
    }
  };

  const renderCommandLine = () => {
    // Active command input mode
    if (commandMode) {
      return (
        <div className="commandline-input-wrap">
          <span className="commandline-colon">:</span>
          <input
            ref={inputRef}
            className="commandline-input"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={onCommandCancel}
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      );
    }

    // Echo of last command (e.g. ":e about.md")
    if (commandText) {
      // Error messages (E123: ...) or fun quit messages
      const isError = /^E\d+:/.test(commandText);
      const isVimCmd = commandText.startsWith(':');
      if (isError || !isVimCmd) {
        return (
          <span className={isError ? 'commandline-echo-error' : 'commandline-echo-cmd'}>
            {commandText}
          </span>
        );
      }
      return (
        <span className="commandline-echo">
          <span className="commandline-echo-cmd">
            {commandText.split(' ')[0]}
          </span>
          {commandText.includes(' ') && (
            <span className="commandline-echo-arg">
              {' '}{commandText.split(' ').slice(1).join(' ')}
            </span>
          )}
        </span>
      );
    }

    // Default: show mode
    return (
      <span className={`commandline-mode ${vimMode.toLowerCase()}`}>
        -- {vimMode} --
      </span>
    );
  };

  return (
    <div className="nvim-statusline-wrap">
      <div className="nvim-statusline-bar">
        <span className="statusline-left">
          <span className="statusline-filename">{activeFile}</span>
          <span className="statusline-ro">[RO]</span>
          <span className="statusline-lines">{lineCount}L</span>
        </span>
        <span className="statusline-right">
          <span className="statusline-pos">{cursorLine || 1},1</span>
          <span className="statusline-pct">{scrollPct}</span>
        </span>
      </div>
      <div className="nvim-commandline">
        {renderCommandLine()}
      </div>
    </div>
  );
};

export default StatusLine;
